import React, { useState, useEffect } from "react";
import { Box, Grid, Typography, Stack, Button, Drawer, IconButton, useTheme, useMediaQuery, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, Avatar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"; // Import Info Icon
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  getTask,
  getTaskSeparateById,
  getWorkType,
  updateTask,
  getRelatedBugs,
  deleteTaskFile,
  getTaskBasicById,
  getTaskHistoryById,
  getTaskCommentsById,
} from "../../services/taskManagement.service";
import AddSubTask from "../../components/TaskManagement/AddSubTask";
import AddTaskModal from "./AddTaskModal";
import { getUser } from "../../services/user.service";
import { getUserProject } from "../../services/taskManagement.service";
import dayjs from "dayjs";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import { useLocation, useNavigate } from "react-router-dom";
import Bugs_Icon from "../../assets/images/bug.png";
import Task_Icon from "../../assets/images/Task_Icon.svg";
import Meeting_Icon from "../../assets/images/Meeting.png";
import Extra_Icon from "../../assets/images/Extra_Icon.svg";
import Testing_Icon from "../../assets/images/Testing_Icon.svg";
import Designing_Icon from "../../assets/images/Designing_Icon.svg";
import Deployment_Icon from "../../assets/images/Deployment_Icon.svg";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../context/AuthProvider";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  PRIORITY,
  STATUS,
  ISSUETYPE,
  VERIFY_STATUS,
} from "../../common/constant/constant";
import { getProjectParentTask } from "../../services/parentTask";
// Import Date and Time pickers
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import {
  getPriorityLabel,
  getStatusLabel,
  getIssueTypeLabel,
  getVerifyStatusLabel,
} from "./utils/helpers";

import WorkLogs from "./components/WorkLog";
import TitleSection from "./components/TitleSection";
import DescriptionSection from "./components/DescriptionSection";
import Attachments from "./components/Attachments";
import SubTasks from "./components/SubTasks";
import Activity from "./components/Activity";
import DetailsPanel from "./components/DetailsPanel";
import { isAdminRole } from "../../utils/roles";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

const TaskDetails = (props) => {
  const location = useLocation();
  const { accessToken } = useAuth();
  const queryParams = new URLSearchParams(location.search);
  const taskId = queryParams.get("taskId");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedData, setSelectedData] = useState({});
  const [isDetailsOpen, setIsDetailsOpen] = useState(!isMobile); // Default based on device

  useEffect(() => {
    setIsDetailsOpen(!isMobile);
  }, [isMobile]);
  const [loading, setLoading] = useState(false);
  const [isHold, setIsHold] = useState(false);
  const [isClientView, setIsClientView] = useState(false);
  const [userData, setUserData] = useState([]);
  const [workTypeData, setWorkTypeData] = useState([]);
  const [parentTasks, setParentTasks] = useState([]);
  const [selectedParentTask, setSelectedParentTask] = useState(null);
  const [subSelectedData, setSubSelectedData] = useState({});
  const [openSubTaskDrawer, setOpenSubTaskDrawer] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userId, setUserId] = useState(null);
  const [viewMode, setViewMode] = useState("View");
  const [relatedBugs, setRelatedBugs] = useState([]);
  const [addBugModalOpen, setAddBugModalOpen] = useState(false);

  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);

  // Pagination State
  const [historyPage, setHistoryPage] = useState(1);
  const [commentsPage, setCommentsPage] = useState(1);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [hasMoreComments, setHasMoreComments] = useState(true);

  // Linked Testing Tasks State
  const [openLinkDialog, setOpenLinkDialog] = useState(false);
  const [testingTasksList, setTestingTasksList] = useState([]);
  const [selectedLinkIds, setSelectedLinkIds] = useState([]);
  const [isFetchingTasks, setIsFetchingTasks] = useState(false);

  const fetchTestingTasks = async () => {
    setIsFetchingTasks(true);
    try {
      const res = await getTask({ type: ISSUETYPE.TESTING, project: selectedData?.project });
      if (res.data && res.data.data) {
        let allTasks = [];
        const taskGroups = res.data.data;
        // Handle both grouped and flat API responses
        if (Array.isArray(taskGroups)) {
          allTasks = taskGroups;
        } else {
          Object.values(taskGroups).forEach(group => {
            if (Array.isArray(group)) allTasks = [...allTasks, ...group];
          });
        }

        // Filter out the current task itself
        allTasks = allTasks.filter(t => t._id !== selectedData._id);
        setTestingTasksList(allTasks);
      }
    } catch (error) {
      console.error("Error fetching testing tasks:", error);
      ToastError("Failed to fetch testing tasks");
    } finally {
      setIsFetchingTasks(false);
    }
  };

  const handleOpenLinkDialog = () => {
    fetchTestingTasks();
    const currentLinkedIds = selectedData?.testingTasks?.map(t => t._id) || [];
    setSelectedLinkIds(currentLinkedIds);
    setOpenLinkDialog(true);
  };

  const handleCloseLinkDialog = () => {
    setOpenLinkDialog(false);
  };

  const handleToggleTaskSelection = (taskId) => {
    setSelectedLinkIds(prevIds => {
      if (prevIds.includes(taskId)) {
        return prevIds.filter(id => id !== taskId);
      } else {
        return [...prevIds, taskId];
      }
    });
  };

  const handleSaveLinks = async () => {
    try {
      const payload = {
        testingTasks: selectedLinkIds
      };

      const formData = new FormData();
      formData.append("bodyData", JSON.stringify(payload));

      const res = await updateTask(selectedData._id, formData);
      if (res.error === false) {
        ToastSuccess("Linked tasks updated successfully");
        GetSeparateTaskId(selectedData._id); // Refresh task details
        handleCloseLinkDialog();
      } else {
        ToastError(res?.message || "Failed to update links");
      }
    } catch (error) {
      console.error("Error updating links:", error);
      ToastError("An error occurred while linking tasks");
    }
  };

  const handleUnlinkTask = async (taskId, e) => {
    e.stopPropagation(); // Prevent opening task details
    // Confirm delete if needed? For now direct unlink for speed as per "Quick remove"
    try {
      const currentLinkedIds = selectedData?.testingTasks?.map(t => t._id) || [];
      const newLinkedIds = currentLinkedIds.filter(id => id !== taskId);

      const payload = {
        testingTasks: newLinkedIds
      };

      const res = await updateTask(selectedData._id, payload);
      if (res.error === false) {
        ToastSuccess("Task unlinked successfully");
        GetSeparateTaskId(selectedData._id); // Refresh task details
      } else {
        ToastError(res?.message || "Failed to unlink task");
      }
    } catch (error) {
      console.error("Error unlinking task:", error);
      ToastError("An error occurred while unlinking task");
    }
  };

  const navigate = useNavigate();

  const filteredStatus = location.state?.filteredStatus || null;
  const filteredMonth = location.state?.selectedMonth || null;
  const filteredYear = location.state?.selectedYear || null;
  const fromClient = location.state?.fromClient || false;
  const isOverAllAdmin = location.state?.isOverAllAdmin || false;
  useEffect(() => {
    const getUserIdFromToken = () => {
      if (!accessToken) return null;
      try {
        const decoded = jwtDecode(accessToken);
        const role = decoded.role;
        const adminStatus = isAdminRole(role) || role === 5;
        setIsAdmin(adminStatus);
        return decoded.id;
      } catch (error) {
        console.error("Invalid token", error);
        return null;
      }
    };

    setUserId(getUserIdFromToken());
  }, [accessToken]);

  const GetSeparateTaskId = async (taskId) => {
    try {
      setLoading(true);
      // Reset Pagination
      setHistoryPage(1);
      setCommentsPage(1);
      setHasMoreHistory(true);
      setHasMoreComments(true);

      // 1. Get Basic Data (Fast)
      const res = await getTaskBasicById(taskId);

      if (res?.data?.error) {
        ToastError(res?.data?.message || "Failed to load task details");
        setLoading(false);
        return;
      }

      const configData = res?.data?.data;
      // Initialize with empty history/comments
      setSelectedData({ ...configData, history: [], comments: [] });
      setIsHold(configData.isHold || false);
      setIsClientView(configData.isclientView || false);
      setSelectedParentTask(configData.parentTask || null);

      setLoading(false); // Screen accessible immediately

      // 2. Load History (Background) - Page 1
      setLoadingHistory(true);
      getTaskHistoryById(taskId, 1, 10)
        .then(histRes => {
          if (!histRes?.data?.error) {
            const historyData = histRes.data.data;
            if (historyData.length < 10) setHasMoreHistory(false);
            setSelectedData(prev => ({ ...prev, history: historyData }));
          } else {
            setHasMoreHistory(false);
          }
        })
        .catch(err => console.error("History load error:", err))
        .finally(() => setLoadingHistory(false));

      // 3. Load Comments (Background) - Page 1
      setLoadingComments(true);
      getTaskCommentsById(taskId, 1, 10)
        .then(commRes => {
          if (!commRes?.data?.error) {
            const commentsData = commRes.data.data;
            if (commentsData.length < 10) setHasMoreComments(false);
            setSelectedData(prev => ({ ...prev, comments: commentsData }));
          } else {
            setHasMoreComments(false);
          }
        })
        .catch(err => console.error("Comments load error:", err))
        .finally(() => setLoadingComments(false));

    } catch (error) {
      console.log("Error in GetSeparateTaskId:", error);
      setLoading(false);
    }
  };

  const loadMoreHistory = async () => {
    if (loadingHistory || !hasMoreHistory || !selectedData?._id) return;
    setLoadingHistory(true);
    try {
      const nextPage = historyPage + 1;
      const res = await getTaskHistoryById(selectedData._id, nextPage, 10);
      if (!res?.data?.error) {
        const newData = res.data.data;
        if (newData.length < 10) setHasMoreHistory(false);
        setSelectedData(prev => ({
          ...prev,
          history: [...(prev.history || []), ...newData]
        }));
        setHistoryPage(nextPage);
      }
    } catch (err) {
      console.error("Load more history error:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadMoreComments = async () => {
    if (loadingComments || !hasMoreComments || !selectedData?._id) return;
    setLoadingComments(true);
    try {
      const nextPage = commentsPage + 1;
      const res = await getTaskCommentsById(selectedData._id, nextPage, 10);
      if (!res?.data?.error) {
        const newData = res.data.data;
        if (newData.length < 10) setHasMoreComments(false);
        setSelectedData(prev => ({
          ...prev,
          comments: [...(prev.comments || []), ...newData]
        }));
        setCommentsPage(nextPage);
      }
    } catch (err) {
      console.error("Load more comments error:", err);
    } finally {
      setLoadingComments(false);
    }
  };



  useEffect(() => {
    if (taskId) {
      GetSeparateTaskId(taskId);
    }
  }, [taskId]);

  const updateTaskData = async (payload) => {
    const formData = new FormData();
    formData.append(
      "bodyData",
      JSON.stringify({
        ...payload,
        assignedToId: userId,
      })
    );

    try {
      await updateTask(selectedData._id, formData);
      GetSeparateTaskId(selectedData._id);
      ToastSuccess("Updated successfully");
      return true;
    } catch (error) {
      console.error(error);
      let errorMessage = error?.data?.data?.message || error?.data?.message || "Failed to update";

      // Simplify verbose time conflict message
      if (
        errorMessage.includes("Time conflict") ||
        errorMessage.includes("User already has work") ||
        errorMessage.includes("overlaps with existing schedule")
      ) {
        // Extract Task ID (Format: "task MEE-123")
        const taskMatch = errorMessage.match(/task\s+([A-Z0-9-]+)/i);
        const taskId = taskMatch ? taskMatch[1] : null;

        // Extract Schedule Details (Format 2: "scheduled on Jan 29... from ... to ...")
        const scheduleMatch = errorMessage.match(
          /scheduled on\s+(.*?)\s+from\s+(.*?)\s+to\s+(.*?)(?:\.|$)/i
        );
        // Extract Simple Time Range (Format 1: "(04:20 AM - 05:25 AM)")
        const simpleTimeMatch = errorMessage.match(/\(([^)]+)\)/);

        if (taskId && scheduleMatch) {
          errorMessage = `Time overlap with ${taskId} on ${scheduleMatch[1]} (${scheduleMatch[2]} - ${scheduleMatch[3]})`;
        } else if (taskId && simpleTimeMatch) {
          errorMessage = `Time overlap with ${taskId} (${simpleTimeMatch[1]})`;
        } else if (scheduleMatch) {
          errorMessage = `Time overlap! Existing work on ${scheduleMatch[1]} (${scheduleMatch[2]} - ${scheduleMatch[3]})`;
        } else if (taskId) {
          errorMessage = `Time overlap with ${taskId}`;
        } else {
          errorMessage = "Time overlap with an existing task. Please check the timings.";
        }
      }

      ToastError(errorMessage);
      return false;
    }
  };

  // Fetch Parent Tasks
  const fetchParentTasks = async () => {
    try {
      if (selectedData?.project) {
        const res = await getProjectParentTask(selectedData.project);
        const configData = res?.data?.data?.map((task) => {
          const projectTypeName = task.projectType?.name
            ? ` (${task.projectType.name})`
            : "";
          const title = task.title ? ` - ${task.title}` : "";
          return {
            ...task,
            label: (task.code || "Untitled Task") + title + projectTypeName,
          };
        });
        setParentTasks(configData || []);
      } else {
        setParentTasks([]);
      }
    } catch (error) {
      console.error("Error fetching parent tasks:", error);
      ToastError("Failed to load parent tasks");
    }
  };

  useEffect(() => {
    if (selectedData?.project) fetchParentTasks();
  }, [selectedData]);

  // Handle Parent Task Change
  const handleParentTaskChange = async (event, task) => {
    if (task) {
      try {
        await updateTaskData({ parentTask: task._id });
        setSelectedParentTask(task);
        // ToastSuccess("Parent task updated successfully");
      } catch (error) {
        console.error("Error updating parent task:", error);
        ToastError("Failed to update parent task");
      }
    }
  };

  // Remove Parent Task
  const handleRemoveParentTask = async () => {
    try {
      await updateTaskData({ parentTask: null });
      setSelectedParentTask(null);
      ToastSuccess("Parent task removed successfully");
    } catch (error) {
      console.error("Error removing parent task:", error);
      ToastError("Failed to remove parent task");
    }
  };

  // Get Parent Task Name by ID
  const getParentTaskName = (parentTaskId) => {
    if (!parentTaskId) return "No Parent Task";
    const parentTask = parentTasks.find((task) => task._id === parentTaskId);
    return parentTask ? parentTask?.label : "";
  };

  const handleNavigate = () => {
    navigate(-1);
  };

  const handleSetEstimated = async (estimatedTime) => {
    await updateTaskData({ estimatedTime });
  };

  const handleRemoveEstimated = async (e) => {
    await updateTaskData({ estimatedTime: "" });
  };

  const handlePriorityChange = async (event, value) => {
    await updateTaskData({ priority: value.value });
  };
  const handleVerifyStatusChange = async (event, value) => {
    let payload = { verifyStatus: value.value, status: STATUS.TODO };
    await updateTaskData(payload);
  };
  const handleClientVerifyStatusChange = async (event, value) => {
    let payload = {
      clientVerifyStatus: value.value,
    };

    if (value.value !== VERIFY_STATUS.REOPEN) {
      payload.clientVerified = true;
    } else {
      payload.status = STATUS.TODO;
    }
    await updateTaskData(payload);
  };

  const handletypeChange = async (event, value) => {
    if (selectedData.taskType !== value.value) {
      await updateTaskData({ issueType: value.value });
    }
  };

  const handleStatusChange = async (event, value) => {
    if (value) {
      await updateTaskData({ status: value.value, StatusChange: true });
    }
  };

  const handleAssignedToChange = async (event, value) => {
    await updateTaskData({
      assignee: value._id,
      taskId,
    });
  };

  const handleDeleteFile = async (values) => {
    try {
      const fileId = values._id;

      const res = await deleteTaskFile(selectedData._id, {
        params: { fileId, userId },
      });

      ToastSuccess(res.data.message);
      GetSeparateTaskId(selectedData._id);
    } catch (error) {
      console.error(error);
      const message =
        error?.data?.message || "Failed to delete file. Please try again.";
      ToastError(message);
    }
  };

  const handleSubTaskEdit = (e, subTaskId) => {
    const selectedSubTask = selectedData?.subTasks.find(
      (task) => task._id === subTaskId
    );
    if (selectedSubTask) {
      setSubSelectedData(selectedSubTask);
      setOpenSubTaskDrawer(true);
      setViewMode("Edit");
    }
  };

  const handleSubTaskView = (e, subTaskId) => {
    const selectedSubTask = selectedData?.subTasks.find(
      (task) => task._id === subTaskId
    );
    if (selectedSubTask) {
      setSubSelectedData(selectedSubTask);
      setOpenSubTaskDrawer(true);
      setViewMode("View");
    }
  };

  const handleSwitchChange = async (e) => {
    const newIsHold = e.target.checked;
    setIsHold(newIsHold);
    await updateTaskData({ isHold: newIsHold });
  };

  const handleClientViewChange = async (e) => {
    const newIsHold = e.target.checked;
    setIsClientView(newIsHold);
    await updateTaskData({ isclientView: newIsHold });
  };

  const handleActualDeadlineDateChange = async (event) => {
    await updateTaskData({ actualDeadlineDate: event.target.value });
  };

  const handleClientDeadlineDateChange = async (event) => {
    await updateTaskData({ clientDeadlineDate: event.target.value });
  };

  const GetWorkTypeData = async () => {
    try {
      const res = await getWorkType();
      const configData = res?.data?.data?.map((e) => {
        return {
          ...e,
          label: e.workType,
        };
      });
      setWorkTypeData(configData);
    } catch (error) {
      console.log(error);
    }
  };

  const getUserData = async () => {
    try {
      const res = await getUserProject(selectedData.project);
      const configData = res.data.data
        .filter((e) => !e.isOverAllAdmin)
        .map((e) => {
          return {
            ...e,
            label: e.name,
          };
        });
      setUserData(configData);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    GetWorkTypeData();
    if (selectedData?.project) getUserData();
  }, [selectedData]);

  const addFile = async (id, files) => {
    setLoading(true);
    if (!files) {
      console.error("No file selected");
      return;
    }
    const formData = new FormData();
    formData.append("file", files);
    formData.append("bodyData", JSON.stringify({ assignedToId: userId }));
    try {
      const res = await updateTask(id, formData);
      setLoading(false);
      ToastSuccess(res.message);
      GetSeparateTaskId(id);
    } catch (error) {
      console.error(error);
      ToastError("File Not Uploaded");
      setLoading(false);
    }
  };

  const handleSubTaskDelete = async (_id) => {
    try {
      const res = await updateTask(selectedData._id, {
        bodyData: JSON.stringify({ subTask: { _id } }),
      });
      ToastSuccess(res.data.message);
      GetSeparateTaskId(selectedData._id);
    } catch (error) {
      ToastError(error?.data?.message);
    }
  };

  const handleAddComments = async (values) => {
    const formData = new FormData();
    let payload = {
      comments: [{ assignedTo: userId, commentText: values }],
    };
    formData.append("bodyData", JSON.stringify(payload));
    try {
      await updateTask(taskId, formData);
      GetSeparateTaskId(taskId);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchRelatedBugs = async () => {
    try {
      const response = await getRelatedBugs(taskId);

      if (response?.data?.data) {
        setRelatedBugs(response.data.data);
      } else {
        setRelatedBugs([]);
      }
    } catch (error) {
      setRelatedBugs([]);
      ToastError("Failed to fetch related bugs");
    }
  };

  useEffect(() => {
    if (taskId) {
      fetchRelatedBugs();
    }
  }, [taskId]);

  const handleAddBugClick = () => {
    setAddBugModalOpen(true);
  };

  const handleBugModalClose = () => {
    setAddBugModalOpen(false);
    fetchRelatedBugs(); // Refresh bugs list after adding
  };

  // Updated options using constants
  const statusOption = [
    { label: "To Do", value: STATUS.TODO },
    { label: "InProgress", value: STATUS.INPROGRESS },
    { label: "QA", value: STATUS.QA },
    { label: "Production", value: STATUS.PRODUCTION },
    { label: "Staging", value: STATUS.STAGING },
    { label: "Pending", value: STATUS.PENDING },
    { label: "UAT", value: STATUS.UAT },
  ];

  let filteredStatusOptions = statusOption;
  if (!isOverAllAdmin) {
    filteredStatusOptions = statusOption.filter(
      (option) => option.value !== STATUS.PENDING && option.value !== STATUS.UAT
    );
  }

  const priorityOption = [
    { label: "Low", value: PRIORITY.LOW },
    { label: "Medium", value: PRIORITY.MEDIUM },
    { label: "High", value: PRIORITY.HIGH },
  ];
  const QAVerifyOption = [{ label: "Reopen", value: VERIFY_STATUS.REOPEN }];
  const clientVerifyOption = [
    { label: "Completed", value: VERIFY_STATUS.COMPLETED },
    { label: "Reopen", value: VERIFY_STATUS.REOPEN },
  ];

  // All type options (full list)
  const allTypeOptions = [
    {
      label: "Task",
      value: ISSUETYPE.TASK,
      icon: (
        <img
          src={Task_Icon}
          alt="Task_Icon"
          style={{
            height: "20px",
            width: "20px",
          }}
        />
      ),
    },
    {
      label: "Bugs",
      value: ISSUETYPE.BUG,
      icon: (
        <img
          src={Bugs_Icon}
          alt="Bugs_Icon"
          style={{
            height: "20px",
            width: "20px",
          }}
        />
      ),
    },
    {
      label: "Meeting",
      value: ISSUETYPE.MEETING,
      icon: (
        <img
          src={Meeting_Icon}
          alt="Meeting_Icon"
          style={{
            height: "20px",
            width: "20px",
          }}
        />
      ),
    },
    {
      label: "Testing",
      value: ISSUETYPE.TESTING,
      icon: (
        <img
          src={Testing_Icon}
          alt="Testing_Icon"
          style={{
            height: "20px",
            width: "20px",
          }}
        />
      ),
    },
    {
      label: "Designing",
      value: ISSUETYPE.DESIGNING,
      icon: (
        <img
          src={Designing_Icon}
          alt="Designing_Icon"
          style={{
            height: "20px",
            width: "20px",
          }}
        />
      ),
    },
    {
      label: "Others",
      value: ISSUETYPE.OTHERS,
      icon: (
        <img
          src={Extra_Icon}
          alt="Others_Icon"
          style={{
            height: "20px",
            width: "20px",
          }}
        />
      ),
    },
    {
      label: "Deployment",
      value: ISSUETYPE.DEPLOYMENT,
      icon: (
        <img
          src={Deployment_Icon}
          alt="Deployment_Icon"
          style={{
            height: "20px",
            width: "20px",
          }}
        />
      ),
    },
  ];

  // Filter type options based on current issue type
  // If current is Bug, show all (but dropdown will be disabled)
  // If current is not Bug, exclude Bugs from options
  const isBugType = selectedData?.issueType === ISSUETYPE.BUG;
  const typeOption = isBugType
    ? allTypeOptions
    : allTypeOptions.filter((opt) => opt.value !== ISSUETYPE.BUG);

  // Helper function to get current display values
  const getCurrentPriorityLabel = () => {
    return getPriorityLabel(selectedData?.priority);
  };

  const getCurrentStatusLabel = () => {
    return getStatusLabel(selectedData?.status);
  };

  const getCurrentIssueTypeLabel = () => {
    return getIssueTypeLabel(selectedData?.issueType);
  };

  const getCurrentVerifyStatusLabel = () => {
    return getVerifyStatusLabel(
      fromClient === true
        ? selectedData?.clientVerifyStatus
        : selectedData?.verifyStatus
    );
  };

  return (
    <div className="addRenderingContainer">
      <Box className="addContainerHeader1">
        <Stack direction="row" spacing={1} alignItems="center">
          {selectedData?.issueType === ISSUETYPE.BUG && (
            <img
              src={Bugs_Icon}
              alt="Bugs_Icon"
              style={{ width: "20px", height: "20px" }}
            />
          )}
          {selectedData?.issueType === ISSUETYPE.TASK && (
            <img
              src={Task_Icon}
              alt="Task_Icon"
              style={{ width: "20px", height: "20px" }}
            />
          )}
          {selectedData?.issueType === ISSUETYPE.TESTING && (
            <img
              src={Testing_Icon}
              alt="Testing_Icon"
              style={{ width: "20px", height: "20px" }}
            />
          )}
          {selectedData?.issueType === ISSUETYPE.DESIGNING && (
            <img
              src={Designing_Icon}
              alt="Designing_Icon"
              style={{ width: "20px", height: "20px" }}
            />
          )}
          {selectedData?.issueType === ISSUETYPE.MEETING && (
            <img
              src={Meeting_Icon}
              alt="Meeting_Icon"
              style={{ width: "20px", height: "20px" }}
            />
          )}
          {selectedData?.issueType === ISSUETYPE.OTHERS && (
            <img
              src={Extra_Icon}
              alt="Extra_Icon"
              style={{ width: "20px", height: "20px" }}
            />
          )}
          {selectedData?.issueType === ISSUETYPE.DEPLOYMENT && (
            <img
              src={Deployment_Icon}
              alt="Deployment_Icon"
              style={{ width: "20px", height: "20px" }}
            />
          )}
          <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>
            {selectedData?.issueType === ISSUETYPE.BUG
              ? "Bug Details"
              : selectedData?.issueType === ISSUETYPE.MEETING
                ? "Meeting Details"
                : selectedData?.issueType === ISSUETYPE.TASK
                  ? "Task Details"
                  : selectedData?.issueType === ISSUETYPE.DEPLOYMENT
                    ? "Deployment Details"
                    : selectedData?.issueType === ISSUETYPE.DESIGNING
                      ? "Designing Details"
                      : selectedData?.issueType === ISSUETYPE.TESTING
                        ? "Testing Details"
                        : selectedData?.issueType === ISSUETYPE.OTHERS
                          ? "Others Details"
                          : ""}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={isDetailsOpen ? "Hide Details" : "Show Details"}>
            <IconButton
              onClick={() => setIsDetailsOpen(!isDetailsOpen)}
              sx={{
                color: isDetailsOpen ? "#055253" : "inherit",
                backgroundColor: isDetailsOpen ? "rgba(5, 82, 83, 0.1)" : "transparent"
              }}
            >
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
          <CloseIcon sx={{ cursor: "pointer" }} onClick={handleNavigate} />
        </Stack>
      </Box>

      <Box className="addContainerCenter">
        <Grid container sx={{ height: "calc(100vh - 180px)" }}>
          <Grid
            item
            xs={12}
            md={isDetailsOpen ? 8 : 12}
            sx={{ padding: 1, height: "100%", overflowY: "auto", transition: "all 0.3s ease" }}
          >
            {selectedData?._id && (
              <Grid sx={{ m: 1 }}>
                <Stack spacing={2}>

                  <TitleSection
                    initialTitle={selectedData?.title || ""}
                    onSave={updateTaskData}
                    selectedData={selectedData}
                  />

                  {/* Show Related Task for Bugs */}
                  {selectedData?.issueType === ISSUETYPE.BUG && selectedData?.relatedTask && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: "#F0F9FF",
                        borderLeft: "4px solid #055253",
                        borderRadius: "4px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        Related to Task
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          cursor: "pointer",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                        onClick={() =>
                          navigate(
                            `/home/taskDetails?taskId=${selectedData.relatedTask}`,
                            {
                              state: {
                                fromClient,
                                filteredStatus,
                                selectedMonth: filteredMonth,
                                selectedYear: filteredYear,
                              },
                            }
                          )
                        }
                      >
                        <img
                          src={Task_Icon}
                          alt="Task"
                          style={{ width: "16px", height: "16px" }}
                        />
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#055253",
                            fontWeight: 600,
                          }}
                        >
                          View Original Task
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>

                <Typography
                  sx={{
                    fontSize: "16px",
                    color: "#181818",
                    mt: 3,
                    fontWeight: 700,
                  }}
                >
                  Description
                </Typography>
                <DescriptionSection
                  initialDescription={selectedData?.description || ""}
                  onSave={updateTaskData}
                />

                {!fromClient && (
                  <WorkLogs
                    workLogsData={selectedData?.workLog || []}
                    historyData={selectedData?.history}
                    selectedData={selectedData}
                    updateTaskData={updateTaskData}
                    isAdmin={isAdmin}
                  />
                )}

                <Attachments
                  files={selectedData?.file || []}
                  onAddFile={addFile}
                  onDeleteFile={handleDeleteFile}
                  loading={loading}
                  taskId={selectedData?._id}
                  userId={userId}
                />

                <SubTasks
                  subTasks={selectedData?.subTasks || []}
                  onSubTaskDelete={handleSubTaskDelete}
                  onSubTaskEdit={handleSubTaskEdit}
                  onSubTaskView={handleSubTaskView}
                />

                {/* Related Bugs Section */}
                {selectedData?.issueType !== ISSUETYPE.BUG && (
                  <Box sx={{ mt: 3 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography
                        sx={{
                          fontSize: "16px",
                          color: "#181818",
                          fontWeight: 700,
                        }}
                      >
                        Related Bugs ({relatedBugs.length})
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleAddBugClick}
                        sx={{
                          backgroundColor: "#055253",
                          "&:hover": { backgroundColor: "#044142" },
                          textTransform: "none",
                        }}
                      >
                        + Add Bug
                      </Button>
                    </Stack>

                    {relatedBugs.length > 0 ? (
                      <Stack spacing={1}>
                        {relatedBugs.map((bug) => (
                          <Box
                            key={bug._id}
                            sx={{
                              p: 2,
                              border: "1px solid #E0E0E0",
                              borderRadius: "8px",
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "#F5F5F5",
                              },
                            }}
                            onClick={() =>
                              navigate(
                                `/home/taskDetails?taskId=${bug._id}`,
                                {
                                  state: {
                                    fromClient,
                                    filteredStatus,
                                    selectedMonth: filteredMonth,
                                    selectedYear: filteredYear,
                                  },
                                }
                              )
                            }
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <img
                                src={Bugs_Icon}
                                alt="Bug"
                                style={{ width: "20px", height: "20px" }}
                              />
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  color: "#181818",
                                }}
                              >
                                {bug.taskNo}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#666",
                                  flex: 1,
                                }}
                              >
                                {bug.title}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: "#999",
                                  mr: 1,
                                }}
                              >
                                {new Date(bug.createdAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: "#999",
                                  width: "80px",
                                  textAlign: "right"
                                }}
                              >
                                {getStatusLabel(bug.status)}
                              </Typography>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#999",
                          fontStyle: "italic",
                          textAlign: "center",
                          py: 2,
                        }}
                      >
                        No bugs reported for this task
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Linked Testing Tasks Section */}
                {selectedData?.issueType === ISSUETYPE.TESTING && (
                  <Box sx={{ mt: 3 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography
                        sx={{
                          fontSize: "16px",
                          color: "#181818",
                          fontWeight: 700,
                        }}
                      >
                        Linked Testing Tasks ({selectedData?.testingTasks?.length || 0})
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleOpenLinkDialog}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#008080',
                          color: '#008080',
                          '&:hover': {
                            borderColor: '#006666',
                            backgroundColor: '#F0FFFF'
                          }
                        }}
                      >
                        Link Tasks
                      </Button>
                    </Stack>

                    {selectedData?.testingTasks?.length > 0 ? (
                      <Stack spacing={1}>
                        {selectedData.testingTasks.map((tTask) => (
                          <Box
                            key={tTask._id}
                            sx={{
                              p: 2,
                              border: "1px solid #E0E0E0",
                              borderRadius: "8px",
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "#F5F5F5",
                              },
                            }}
                            onClick={() =>
                              navigate(
                                `/home/taskDetails?taskId=${tTask._id}`,
                                {
                                  state: {
                                    fromClient,
                                    filteredStatus,
                                    selectedMonth: filteredMonth,
                                    selectedYear: filteredYear,
                                  },
                                }
                              )
                            }
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              justifyContent="space-between"
                            >
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ overflow: "hidden" }}>
                                <img
                                  src={Testing_Icon}
                                  alt="Testing"
                                  style={{ width: "20px", height: "20px" }}
                                />
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    color: "#181818",
                                  }}
                                >
                                  {tTask.taskNo}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    color: "#666",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                  }}
                                >
                                  {tTask.title}
                                </Typography>
                              </Stack>
                              {/* <Tooltip title="Unlink Task">
                                <IconButton size="small" onClick={(e) => handleUnlinkTask(tTask._id, e)}>
                                  <DeleteOutlineIcon fontSize="small" color="error" />
                                </IconButton>
                              </Tooltip> */}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#999",
                          fontStyle: "italic",
                          textAlign: "center",
                          py: 2,
                        }}
                      >
                        No testing tasks linked to this task
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Related to Task (Reverse Link) Section */}
                {selectedData?.parentTestingTaskDetails && (
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        color: "#181818",
                        fontWeight: 700,
                        mb: 2,
                      }}
                    >
                      Associated Testing Task
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        border: "1px solid #E0E0E0",
                        borderRadius: "8px",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#F5F5F5",
                        },
                        borderLeft: "4px solid #008080",
                      }}
                      onClick={() =>
                        navigate(
                          `/home/taskDetails?taskId=${selectedData.parentTestingTaskDetails._id}`,
                          {
                            state: {
                              fromClient,
                              filteredStatus,
                              selectedMonth: filteredMonth,
                              selectedYear: filteredYear,
                            },
                          }
                        )
                      }
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#008080",
                          }}
                        >
                          View Testing Task
                        </Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#181818", ml: 1 }}>
                          {selectedData.parentTestingTaskDetails.taskNo}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#666",
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {selectedData.parentTestingTaskDetails.title}
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>
                )}

                <Activity
                  selectedData={selectedData}
                  currentUser={userId}
                  GetSeparateTaskId={GetSeparateTaskId}
                  fromClient={fromClient}
                  onAddComment={handleAddComments}
                  loadingHistory={loadingHistory}
                  loadingComments={loadingComments}
                  loadMoreHistory={loadMoreHistory}
                  loadMoreComments={loadMoreComments}
                  hasMoreHistory={hasMoreHistory}
                  hasMoreComments={hasMoreComments}
                />
              </Grid>
            )}
          </Grid>



          {/* Desktop Sidebar (Collapsible Grid) */}
          {!isMobile && isDetailsOpen && (
            <Grid
              item
              md={4}
              sx={{
                padding: 1,
                paddingRight: 2,
                height: "100%",
                overflowY: "auto",
                overflowX: "hidden",
                borderLeft: "1px solid #e0e0e0",
                transition: "all 0.3s ease"
              }}
            >
              {selectedData?._id && (
                <DetailsPanel
                  selectedData={selectedData}
                  fromClient={fromClient}
                  userData={userData}
                  parentTasks={parentTasks}
                  onAssignedToChange={handleAssignedToChange}
                  onParentTaskChange={handleParentTaskChange}
                  onRemoveParentTask={handleRemoveParentTask}
                  onPriorityChange={handlePriorityChange}
                  onVerifyStatusChange={handleVerifyStatusChange}
                  onClientVerifyStatusChange={handleClientVerifyStatusChange}
                  onTypeChange={handletypeChange}
                  onStatusChange={handleStatusChange}
                  onSwitchChange={handleSwitchChange}
                  onClientChange={handleClientViewChange}
                  onSetEstimated={handleSetEstimated}
                  onRemoveEstimated={handleRemoveEstimated}
                  statusOption={filteredStatusOptions}
                  priorityOption={priorityOption}
                  typeOption={typeOption}
                  clientVerifyOption={clientVerifyOption}
                  QAVerifyOption={QAVerifyOption}
                  getCurrentStatusLabel={getCurrentStatusLabel}
                  getCurrentPriorityLabel={getCurrentPriorityLabel}
                  getCurrentIssueTypeLabel={getCurrentIssueTypeLabel}
                  getCurrentVerifyStatusLabel={getCurrentVerifyStatusLabel}
                  getParentTaskName={getParentTaskName}
                  onActualDeadlineDateChange={handleActualDeadlineDateChange}
                  onClientDeadlineDateChange={handleClientDeadlineDateChange}
                />
              )}
            </Grid>
          )}
        </Grid>

        {/* Mobile Sidebar (Drawer) */}
        <Drawer
          anchor="right"
          open={isMobile && isDetailsOpen}
          onClose={() => setIsDetailsOpen(false)}
          PaperProps={{
            sx: { width: "85%", padding: 2 }
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">Task Details</Typography>
            <IconButton onClick={() => setIsDetailsOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          {selectedData?._id && (
            <DetailsPanel
              selectedData={selectedData}
              fromClient={fromClient}
              userData={userData}
              parentTasks={parentTasks}
              onAssignedToChange={handleAssignedToChange}
              onParentTaskChange={handleParentTaskChange}
              onRemoveParentTask={handleRemoveParentTask}
              onPriorityChange={handlePriorityChange}
              onVerifyStatusChange={handleVerifyStatusChange}
              onClientVerifyStatusChange={handleClientVerifyStatusChange}
              onTypeChange={handletypeChange}
              onStatusChange={handleStatusChange}
              onSwitchChange={handleSwitchChange}
              onClientChange={handleClientViewChange}
              onSetEstimated={handleSetEstimated}
              onRemoveEstimated={handleRemoveEstimated}
              statusOption={filteredStatusOptions}
              priorityOption={priorityOption}
              typeOption={typeOption}
              clientVerifyOption={clientVerifyOption}
              QAVerifyOption={QAVerifyOption}
              getCurrentStatusLabel={getCurrentStatusLabel}
              getCurrentPriorityLabel={getCurrentPriorityLabel}
              getCurrentIssueTypeLabel={getCurrentIssueTypeLabel}
              getCurrentVerifyStatusLabel={getCurrentVerifyStatusLabel}
              getParentTaskName={getParentTaskName}
              onActualDeadlineDateChange={handleActualDeadlineDateChange}
              onClientDeadlineDateChange={handleClientDeadlineDateChange}
            />
          )}
        </Drawer>
      </Box>
      {
        openSubTaskDrawer && (
          <AddSubTask
            open={openSubTaskDrawer}
            onClose={() => setOpenSubTaskDrawer(false)}
            mode={viewMode}
            parentTaskId={selectedData?._id}
            workTypeData={workTypeData}
            userData={userData}
            subTaskData={subSelectedData}
            GetSeparateTaskId={GetSeparateTaskId}
          />
        )
      }

      {
        addBugModalOpen && (
          <AddTaskModal
            open={addBugModalOpen}
            onClose={handleBugModalClose}
            formType="add"
            fetchTasks={fetchRelatedBugs}
            defaultIssueType={ISSUETYPE.BUG}
            isRelatedBug={true}
            preFilledData={{
              project: selectedData?.project,
              parentTask: selectedData?.parentTask,
              assignee: selectedData?.assignedTo,
              actualDeadlineDate: selectedData?.deadLineDate,
              priority: selectedData?.priority,
              relatedTask: selectedData?._id,
              title: selectedData?.title,
              description: selectedData?.description,
              estimatedTime: selectedData?.estimatedTime,
            }}
          />
        )
      }

      {/* Linked Testing Tasks Dialog */}
      <Dialog
        open={openLinkDialog}
        onClose={handleCloseLinkDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          Link Testing Tasks
        </DialogTitle>
        <DialogContent sx={{ pt: 2, height: '400px', overflowY: 'auto' }}>
          {isFetchingTasks ? (
            <Stack alignItems="center" justifyContent="center" height="100%">
              <Typography>Loading tasks...</Typography>
            </Stack>
          ) : testingTasksList.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" height="100%">
              <Typography>No other testing tasks found.</Typography>
            </Stack>
          ) : (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {testingTasksList.map(task => (
                <Box
                  key={task._id}
                  sx={{
                    p: 1,
                    border: '1px solid #eee',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:hover': { bgcolor: '#FAFAFA' }
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedLinkIds.includes(task._id)}
                        onChange={() => handleToggleTaskSelection(task._id)}
                        color="primary"
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                  <Stack flex={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="subtitle2" fontWeight="600">
                        {task.taskNo}
                      </Typography>
                      <Typography variant="caption" sx={{
                        bgcolor: '#e3f2fd',
                        color: '#1976d2',
                        px: 1,
                        borderRadius: 1
                      }}>
                        {getStatusLabel(task.status)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" noWrap sx={{ maxWidth: '400px' }}>
                      {task.title}
                    </Typography>
                  </Stack>
                  <Avatar
                    sx={{ width: 24, height: 24, fontSize: 10 }}
                    src={task?.assignee_info?.file?.location}
                  >
                    {task?.assignee_info?.firstName?.[0] || "?"}
                  </Avatar>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button onClick={handleCloseLinkDialog} color="inherit">Cancel</Button>
          <Button onClick={handleSaveLinks} variant="contained" color="primary">
            Save & Link
          </Button>
        </DialogActions>
      </Dialog>
    </div >
  );
};

export default TaskDetails;
